/**
 * UI-1 — the tested logic behind the broker-credentials form and the login-error
 * message.
 *
 * The DOM wiring lives in static/live.html (a small modal), but every DECISION —
 * what the server said, whether the form is valid, what status to show, and how
 * to write server text into the page safely — lives here so it can be unit
 * tested. The page had no way at all to create a broker_credentials row; the
 * only writer (the OAuth callback) refuses to arm a login until one exists
 * (NC-39 §4.3), and POST /api/broker/credentials had zero callers.
 */

/** A minimal element sink — just the two properties the XSS test cares about. */
export interface TextSink {
  textContent?: string | null;
  innerHTML?: string;
}

/**
 * Write untrusted text into an element as TEXT, never HTML (UI-1 §3.1 TRAP).
 *
 * NC-21 flags live.html innerHTML sinks fed by broker/backend strings as a live
 * XSS concern on the page that holds the JWT and places real orders. The fix
 * that finally SURFACES the server's message must not become the injection
 * point, so the one sink for server-supplied text is textContent — a hostile
 * `<img onerror>` lands as inert characters, never a parsed element.
 */
export function setText(el: TextSink | null | undefined, text: string): void {
  if (!el) return;
  el.textContent = text;
}

/**
 * The server's own error wording, or a fallback.
 *
 * UI-1 §2: live.html turned every non-2xx from /api/flattrade-auth-url into
 * "Login start failed (are you signed in?)", discarding NC-39's carefully-worded
 * 409 ("Save your Flattrade api_key and api_secret before logging in") — pointing
 * a credential-less user at a session problem that does not exist. Prefer the
 * server's `detail`; keep the generic text only when there is none.
 *
 * FastAPI puts a string in `detail` for our endpoints; a non-string detail
 * (validation errors are lists/objects) is NOT rendered raw — it falls back,
 * because an object stringified into the UI is noise, not an instruction.
 */
export function serverDetail(body: unknown, fallback: string): string {
  if (body && typeof body === "object") {
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.trim()) return detail;
  }
  return fallback;
}

export interface CredentialForm {
  apiKey?: string;
  apiSecret?: string;
  clientId?: string;
}

export interface CredentialPayload {
  api_key: string;
  api_secret: string;
  client_id?: string;
}

export type ValidationResult =
  | { ok: true; payload: CredentialPayload }
  | { ok: false; error: string };

/**
 * Validate the form and build the POST payload.
 *
 * UI-1 §3.2:
 *  - api_key and api_secret are REQUIRED TOGETHER (acceptance 5). The endpoint
 *    allows a partial save, but a row with api_key and no api_secret passes the
 *    save and then FAILS NC-39's arm check — the user "succeeds" and then cannot
 *    log in. So the pair is all-or-nothing here.
 *  - client_id is optional (the OAuth callback records the authoritative one at
 *    login).
 *  - egress_ip is NOT a field. It is operator-assigned from the static-IP pool
 *    (CO-2) and the client registers that exact IP on their own Flattrade key; a
 *    user free-typing it silently breaks their orders (acceptance 6). The form
 *    never offers it — the CLI / operator path owns it.
 *
 * Secrets are WRITE-ONLY: nothing here ever reads a stored secret back; the user
 * types both fields to set or change them.
 */
export function validateCredentials(form: CredentialForm): ValidationResult {
  const apiKey = (form.apiKey || "").trim();
  const apiSecret = (form.apiSecret || "").trim();
  const clientId = (form.clientId || "").trim();

  if (!apiKey && !apiSecret) {
    return { ok: false, error: "Enter your Flattrade API key and secret." };
  }
  if (!apiKey || !apiSecret) {
    return {
      ok: false,
      error:
        "API key and secret must be saved together — a key without its secret " +
        "cannot complete a login.",
    };
  }
  const payload: CredentialPayload = { api_key: apiKey, api_secret: apiSecret };
  if (clientId) payload.client_id = clientId;
  return { ok: true, payload };
}

/**
 * A one-line, secret-free status from GET /api/broker/credentials.
 *
 * The GET already masks api_key and omits api_secret; this never reintroduces a
 * secret even if a future/hostile response carried one (acceptance 4) — it reads
 * only the fields it names.
 */
export function statusText(get: unknown): string {
  const g = (get && typeof get === "object" ? get : {}) as Record<string, unknown>;
  if (!g.configured) {
    return "Not configured — save your Flattrade API key & secret to enable login.";
  }
  const key = typeof g.api_key === "string" && g.api_key ? g.api_key : "(set)";
  const session = g.has_session ? "logged in today" : "not logged in yet";
  const client = typeof g.client_id === "string" && g.client_id ? `, client ${g.client_id}` : "";
  return `Configured — API key ${key}, ${session}${client}.`;
}
